import {
    initHeaderScrollLock,
    lockPageScroll,
    unlockPageScroll,
} from './page-scroll-lock';

const getSidebarState = () => {
    if (!window.__mdaSidebarState) {
        window.__mdaSidebarState = {
            openStack: [],
            sharedOverlay: null,
        };
    }

    return window.__mdaSidebarState;
};

export class Sidebar {
    static CLOSE_DURATION_MS = 1020;

    static lockPageScroll = lockPageScroll;
    static unlockPageScroll = unlockPageScroll;
    static initHeaderScrollLock = initHeaderScrollLock;

    static getSharedOverlay() {
        const state = getSidebarState();

        if (state.sharedOverlay) {
            return state.sharedOverlay;
        }

        const overlay = document.createElement('div');
        overlay.classList.add(
            'fixed',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'z-200',
            'bg-grey-900/60',
            'cursor-pointer',
            'opacity-0',
            'pointer-events-none',
            'transition-opacity',
            'duration-500',
            'ease-cubic',
            '[&.-active]:opacity-100',
            '[&.-interactive]:pointer-events-auto'
        );
        overlay.setAttribute('data-sidebar-shared-overlay', '');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.addEventListener('click', Sidebar.handleSharedOverlayClick);

        document.body.insertBefore(overlay, document.body.firstChild);
        state.sharedOverlay = overlay;

        return overlay;
    }

    static handleSharedOverlayClick = () => {
        const { openStack } = getSidebarState();

        for (let index = openStack.length - 1; index >= 0; index -= 1) {
            const sidebar = openStack[index];

            if (sidebar.isOpen) {
                sidebar.close();
                return;
            }
        }
    };

    static registerOpen(sidebar) {
        const state = getSidebarState();

        state.openStack = state.openStack.filter(item => item !== sidebar);
        state.openStack.push(sidebar);
        Sidebar.syncSharedOverlay();
    }

    static unregisterOpen(sidebar) {
        const state = getSidebarState();

        state.openStack = state.openStack.filter(item => item !== sidebar);
        Sidebar.syncSharedOverlay();
    }

    static syncSharedOverlay() {
        const overlay = Sidebar.getSharedOverlay();
        const hasOpen = getSidebarState().openStack.length > 0;

        overlay.classList.toggle('-active', hasOpen);
        overlay.classList.toggle('-interactive', hasOpen);
    }

    constructor(options = {}) {
        this.options = {
            lockScroll: true,
            ...options,
        };
        this.init();
    }

    init() {
        this.root = null;
        this.content = null;
        this.isOpen = false;
        this.isClosing = false;
        this.closeTimer = null;
        this.openRafId = null;

        this.create();
        this.events();
    }

    create = () => {
        const rootClassNames = [
            'fixed',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'z-200',
            'pointer-events-none',
            'group/sidebar',
            ...(this.options.rootClassNames || []),
        ];

        this.root = document.createElement('div');
        this.root.classList.add(...rootClassNames);
        this.root.setAttribute('aria-hidden', 'true');

        const contentClassNames = [
            'relative',
            'z-20',
            'flex',
            'justify-end',
            'h-full',
            'translate-x-full',
            'pointer-events-none',
            '[&>div]:pointer-events-auto',
            'transition-transform',
            'duration-500',
            'group-[.-animating]/sidebar:duration-1000',
            'ease-cubic',
            'group-[.-active]/sidebar:translate-x-0',
            'group-[.-closing]/sidebar:translate-x-0',
            ...(this.options.contentClassNames || []),
        ];

        this.content = document.createElement('div');
        this.content.classList.add(...contentClassNames);
        this.root.appendChild(this.content);

        document.body.appendChild(this.root);
    };

    bringToFront() {
        if (this.root?.parentNode) {
            document.body.appendChild(this.root);
        }
    }

    cancelOpenAnimation = () => {
        if (this.openRafId === null) {
            return;
        }

        cancelAnimationFrame(this.openRafId);
        this.openRafId = null;
    };

    clearCloseTimer = () => {
        if (this.closeTimer === null) {
            return;
        }

        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
    };

    finishCloseAnimation = () => {
        this.clearCloseTimer();
        this.root.classList.remove('-animating');
        this.isClosing = false;
    };

    runOpenAnimation = () => {
        this.cancelOpenAnimation();
        this.root.classList.remove('-active', '-closing', '-animating');
        void this.root.offsetWidth;

        this.openRafId = requestAnimationFrame(() => {
            this.openRafId = null;

            if (!this.isOpen) {
                return;
            }

            this.root.classList.add('-active');
        });
    };

    setAriaHidden(isHidden) {
        this.root?.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
    }

    open = () => {
        const isVisible = this.root.classList.contains('-active');

        if (this.isOpen && isVisible && !this.isClosing) {
            return;
        }

        if (this.isClosing) {
            this.clearCloseTimer();
            this.cancelOpenAnimation();
            this.content.removeEventListener(
                'transitionend',
                this.handleCloseTransitionEnd
            );
            this.root.classList.remove('-closing', '-animating');
            this.isClosing = false;
        }

        this.isOpen = true;
        this.setAriaHidden(false);
        this.bringToFront();
        Sidebar.registerOpen(this);
        this.runOpenAnimation();

        if (this.options.lockScroll) {
            Sidebar.lockPageScroll();
        }
    };

    handleCloseTransitionEnd = event => {
        if (event.target !== this.content || event.propertyName !== 'transform') {
            return;
        }

        this.content.removeEventListener(
            'transitionend',
            this.handleCloseTransitionEnd
        );
        this.finishCloseAnimation();
    };

    close = () => {
        if (!this.isOpen || this.isClosing) {
            return;
        }

        this.isOpen = false;
        this.setAriaHidden(true);
        this.isClosing = true;
        this.cancelOpenAnimation();
        Sidebar.unregisterOpen(this);

        if (this.options.lockScroll) {
            Sidebar.unlockPageScroll();
        }

        this.root.classList.add('-animating');
        this.root.classList.remove('-active');
        this.root.classList.add('-closing');

        requestAnimationFrame(() => {
            this.root.classList.remove('-closing');
        });

        this.content.addEventListener(
            'transitionend',
            this.handleCloseTransitionEnd
        );
        this.closeTimer = window.setTimeout(() => {
            this.content.removeEventListener(
                'transitionend',
                this.handleCloseTransitionEnd
            );
            this.finishCloseAnimation();
        }, Sidebar.CLOSE_DURATION_MS + 20);
    };

    events = () => {
        window.addEventListener('click', e => {
            const closeTrigger = e.target.closest('[js-close-sidebar]');

            if (closeTrigger && this.root.contains(closeTrigger)) {
                this.close();
            }
        });
    };
}
